// utils/fileOperations.ts
// 添付ファイルのプレビュー・ダウンロード共通処理

import { isInFlutter } from './flutterDetection';
import { blobToBase64 } from './blobUtils';
import { followAPI } from '../services/follow-api';
import { message } from 'antd';

interface AttachFileParams {
  attachfileno: number;
  filename: string;
}

/**
 * 添付ファイルをプレビュー表示する
 * - Flutter環境: ネイティブ側でプレビューを開く
 * - ブラウザ: 新規タブで表示
 */
export const previewAttachFile = async (params: AttachFileParams): Promise<void> => {
  if (!params.attachfileno) {
    message.warning('アップロード前のファイルはプレビューできません');
    return;
  }
  // Safari はawait後のwindow.open()をポップアップブロッカーでブロックするため
  // ユーザー操作の同期コンテキスト内でウィンドウを開く
  const newWindow = isInFlutter() ? null : window.open('', '_blank');
  try {
    const blob = await followAPI.downloadAttachment({
      attachfileno: params.attachfileno,
      download: false,
      filename: params.filename,
    });
    if (isInFlutter()) {
      const base64 = await blobToBase64(blob);
      window.FlutterCamera?.postMessage(JSON.stringify({
        action: 'preview',
        filename: params.filename || 'file',
        mimeType: blob.type,
        data: base64,
      }));
    } else {
      const url = URL.createObjectURL(blob);
      if (newWindow) {
        newWindow.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  } catch {
    newWindow?.close();
    message.error('ファイルのプレビューに失敗しました');
  }
};

/**
 * 添付ファイルをダウンロードする
 * - Flutter環境: ネイティブ側でShare Sheetを表示
 * - ブラウザ: アンカータグ経由でダウンロード
 */
export const downloadAttachFile = async (params: AttachFileParams): Promise<void> => {
  if (!params.attachfileno) {
    message.warning('アップロード前のファイルはダウンロードできません');
    return;
  }
  try {
    const blob = await followAPI.downloadAttachment({
      attachfileno: params.attachfileno,
      download: true,
      filename: params.filename,
    });
    if (isInFlutter()) {
      const base64 = await blobToBase64(blob);
      window.FlutterCamera?.postMessage(JSON.stringify({
        action: 'download',
        filename: params.filename || 'download',
        mimeType: blob.type,
        data: base64,
      }));
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = params.filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch {
    message.error('ファイルのダウンロードに失敗しました');
  }
};
